import {
  getTextPage,
  PlatzBlobView,
  SourceLocationType,
  TextPageView,
  TextSourceLocation,
  updateLastViewedPage,
  useGetTextPage,
} from "../api-generated";
import SourceHeader from "./SourceHeader";
import Blob from "./Blob";
import PaginationControls from "./PaginationControls";
import CenteredLoading from "./Loading";
import ErrorLoading from "./ErrorLoading";
import { isWordToken, useSourceUrlDetails } from "../utils";
import { getSourceQueryKey } from "../domain_data";
import { useKeyPressEvent, usePrefetchAdjacentPages } from "../hooks";
import { useCallback, useEffect, useMemo } from "react";
import useMultiSelect, { MultiSelectableBlob } from "../hooks/useMultiSelect";
import clsx from "clsx";
import MultiSelectMenu from "./MultiSelectMenu";
import useNavigableLines, { NavLine } from "../hooks/useNavigableLines";

interface Props {
  source: TextPageView;
  currentPageNb: number;
  onChangePage: (pageNb: number) => void;
}

const TextSource: React.FC<Props> = ({
  source,
  currentPageNb,
  onChangePage,
}) => {
  const location: TextSourceLocation = {
    type: SourceLocationType.text_page,
    sourceId: source.id,
    pageNumber: source.pageNb,
  };

  const getEmptyMultiSelectState = useCallback(
    (): number[][][] => [Array(source.blobs.length).fill([])],
    [JSON.stringify(source.blobs)]
  );

  const multiSelectableBlobGroups: MultiSelectableBlob[][] = useMemo(
    () => [source.blobs.map((blob) => blob.tokens)],
    [JSON.stringify(source.blobs)]
  );

  const {
    multiSelectedWords,
    multiSelectTokenIndeces,
    setIsMultiSelectOn,
    closeMultiSelect,
    isMultiSelectOn,
    handleToggleMultiSelect,
    removeWordFromMultiSelect,
  } = useMultiSelect(multiSelectableBlobGroups, getEmptyMultiSelectState);

  const navLines: NavLine[] = useMemo(
    () =>
      source.blobs.map((b: PlatzBlobView) => ({
        tokens: b.tokens.map((t) => ({
          canJumpTo:
            isWordToken(t) &&
            //TODO: !
            (t.word!.status === "is_learning" || t.word!.status === "unopened"),
          isNavable: isWordToken(t),
        })),
      })),
    [JSON.stringify(source.blobs)]
  );

  const {
    focusPrevJumpableToken,
    focusNextJumpableToken,
    focusPrevLine,
    focusNextLine,
    focusPrevWord,
    focusNextWord,
    focusSpecificToken,
    focusedTokenIndex,
    focusedLineIndex,
  } = useNavigableLines(navLines);

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "j") {
        focusNextLine();
      } else if (event.key === "k") {
        focusPrevLine();
      } else if (event.key === "w") {
        focusNextWord();
      } else if (event.key === "b") {
        focusPrevWord();
      } else if (event.key === "n") {
        focusNextJumpableToken();
      } else if (event.key === "N" && event.shiftKey) {
        focusPrevJumpableToken();
      }
    },
    [
      focusPrevJumpableToken,
      focusNextJumpableToken,
      focusPrevLine,
      focusNextLine,
      focusPrevWord,
      focusNextWord,
    ]
  );

  useKeyPressEvent(
    {
      id: "text-source",
      priority: 0,
      useCallbackGeneratedCB: handleKeyEvent,
    },
    true
  );

  return (
    <>
      <SourceHeader sourceName={source.name} sourceType="text" />
      {source.blobs.map((blob, lineIndex) => (
        <Blob
          key={`${location}-{lineIndex}`}
          blob={blob}
          location={{ ...location, lineIndex } as TextSourceLocation}
          tokensInMultiSelect={multiSelectTokenIndeces[0][lineIndex]}
          onTokenHover={(tokenIndex) =>
            focusSpecificToken(lineIndex, tokenIndex)
          }
          focusedTokenIndex={
            lineIndex === focusedLineIndex ? focusedTokenIndex : undefined
          }
          onTokenClick={(_, tokenIndex) =>
            handleToggleMultiSelect({
              blobGroupIndex: 0,
              blobIndex: lineIndex,
              tokenIndex,
            })
          }
        />
      ))}

      <div className="flex justify-between gap-4 pt-2">
        <div className="w-1/3">
          <PaginationControls
            sourceId={source.id}
            onChangePage={onChangePage}
            currentPageNb={currentPageNb}
            totalPageNumber={source.totalNbPages}
          />
        </div>

        <div className="w-1/3">
          <div>
            <button
              className={clsx([
                "font-bold font-xs text-white bg-blue-500 rounded active:bg-blue-800 align-middle py-1 px-4",
              ])}
              onClick={
                isMultiSelectOn
                  ? closeMultiSelect
                  : () => setIsMultiSelectOn(true)
              }
            >
              MS
            </button>
          </div>
        </div>

        <div className="w-1/3">
          {isMultiSelectOn && (
            <MultiSelectMenu
              wordsSelected={multiSelectedWords}
              onClickRemove={removeWordFromMultiSelect}
              onCloseMenu={closeMultiSelect}
              location={location}
            />
          )}
        </div>
      </div>
    </>
  );
};

const Wrapped: React.FC = () => {
  const { sourceId, pageNb: pageNbFromUrl, changePage } = useSourceUrlDetails();
  const queryKey = getSourceQueryKey(sourceId!, pageNbFromUrl);
  const {
    data: page,
    isLoading,
    isError,
    error,
  } = useGetTextPage(
    sourceId!,
    { page: pageNbFromUrl },
    {
      query: {
        queryKey,
        keepPreviousData: true,
      },
    }
  );

  useEffect(() => {
    if (pageNbFromUrl === undefined && page?.pageNb !== undefined) {
      changePage(page.pageNb);
    }
  }, [pageNbFromUrl, page?.pageNb]);

  usePrefetchAdjacentPages({
    pageNb: page?.pageNb,
    totalNbPages: page?.totalNbPages,
    sourceId: sourceId!,
    getQueryKey: getSourceQueryKey,
    fetchSource: getTextPage,
  });

  useEffect(() => {
    if (page !== undefined) {
      updateLastViewedPage(page.id, { lastViewedPage: page.pageNb });
    }
  }, [page?.pageNb]);

  if (isLoading) {
    return <CenteredLoading />;
  }

  if (isError) {
    return <ErrorLoading error={error} />;
  }

  return (
    <TextSource
      source={page}
      currentPageNb={page.pageNb}
      onChangePage={changePage}
    />
  );
};

export default Wrapped;
